import { useContext, useEffect, useMemo, useState } from "react"
import ReactDOMServer from "react-dom/server"
import L, { divIcon } from "leaflet"
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet"
import { Card, IconButton, Modal } from "@mui/material"
import { Close, Map as MapIcon } from "@mui/icons-material"
import { MdMyLocation } from "react-icons/md"
import { Spinner } from "../../../componentes/Spinner"
import { ContextoGeral } from "../../../contextos/ContextoGeral"
import ServicoAPI from "../../../servicos/ServicoAPI"

/** Extrai pontos com coordenadas válidas da cotação. */
export function pontosMapaCotacao(enderecos) {
	if (!Array.isArray(enderecos)) return []
	return enderecos
		.map((e, idx) => {
			const la = Number(e["e.la"] ?? e.la)
			const lo = Number(e["e.lo"] ?? e.lo)
			if (!Number.isFinite(la) || !Number.isFinite(lo) || (la === 0 && lo === 0)) return null
			return {
				key: e["es.ponto"] ?? e["e.id"] ?? idx,
				ponto: e["es.ponto"] ?? idx + 1,
				rua: e["e.rua"] || "",
				la,
				lo,
				position: [la, lo],
			}
		})
		.filter(Boolean)
}

/**
 * Decide se a cotação deve oferecer mapa.
 * Prioridade: flag da cotação (API) → config por tipo do serviço → config flat.
 */
export function deveExibirMapaCotacao(cotacao, configCotacao, dados) {
	if (cotacao?.exibirMapa !== undefined && cotacao?.exibirMapa !== null) {
		return !!cotacao.exibirMapa
	}
	const tipo =
		cotacao?.["s.mototaxi"] ??
		cotacao?.tipoVeiculo ??
		cotacao?.siglaTipoVeiculo ??
		cotacao?.["s.tipo"] ??
		cotacao?.["s.tipoVec"] ??
		dados?.siglaTipoVeiculo
	if (tipo && configCotacao?.cotacao?.[tipo]) {
		const v = configCotacao.cotacao[tipo]?.telaCotacoes?.endereco?.exibirMapa?.exibicao
		if (v !== undefined) return !!v
	}
	const flat = configCotacao?.endereco?.exibirMapa?.exibicao
	if (flat !== undefined) return !!flat
	return false
}

/** Flag vinda de /servicoExecucao (config serviços em execução). */
export function deveExibirMapaServico(servico) {
	return !!servico?.exibirMapa
}

/**
 * Busca polyline via PHP (POST /calcularRotaCotacao) → OSRM no servidor.
 * Evita chamada direta do celular ao OSRM (bloqueada fora dos IPs da Mapp).
 */
export async function buscarPolylineRota(dados, pontos, tipoVeiculo = "M") {
	if (!dados?.idProf || !dados?.token || !Array.isArray(pontos) || pontos.length < 2) {
		return []
	}

	const res = await ServicoAPI.calcularRotaCotacao(dados, { pontos, tipoVeiculo })
	if (!res?.resultado || !Array.isArray(res?.dados?.polyline)) {
		throw new Error(res?.mensagem || "Sem rota")
	}
	return res.dados.polyline.filter(
		(pt) => Array.isArray(pt) && pt.length >= 2 && Number.isFinite(Number(pt[0])) && Number.isFinite(Number(pt[1]))
	)
}

const cachePolyline = new Map()

function chaveCache(pontos, tipoVeiculo) {
	return `${tipoVeiculo}|${pontos.map((p) => `${p.la},${p.lo}`).join(";")}`
}

function iconNumero(n, cor = "var(--primaria)") {
	return divIcon({
		html: ReactDOMServer.renderToString(
			<div
				style={{
					width: 28,
					height: 28,
					borderRadius: "50%",
					background: cor,
					color: "#fff",
					fontWeight: 700,
					fontSize: 13,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					border: "2px solid #fff",
					boxShadow: "0 1px 4px rgba(0,0,0,.35)",
				}}
			>
				{n}
			</div>
		),
		className: "iconesMapaCotacao",
		iconSize: [28, 28],
		iconAnchor: [14, 14],
	})
}

function iconVoce() {
	return divIcon({
		html: ReactDOMServer.renderToString(
			<div
				style={{
					width: 18,
					height: 18,
					borderRadius: "50%",
					background: "#1976d2",
					border: "3px solid #fff",
					boxShadow: "0 0 0 2px #1976d2, 0 1px 4px rgba(0,0,0,.35)",
				}}
			/>
		),
		className: "iconesMapaCotacao",
		iconSize: [18, 18],
		iconAnchor: [9, 9],
	})
}

function FitEInvalidate({ positions, open }) {
	const map = useMap()
	useEffect(() => {
		if (!open || !positions?.length) return
		const t = setTimeout(() => {
			map.invalidateSize()
			if (positions.length === 1) {
				map.setView(positions[0], 15)
			} else {
				map.fitBounds(L.latLngBounds(positions), { padding: [36, 36], maxZoom: 15 })
			}
		}, 80)
		return () => clearTimeout(t)
	}, [map, positions, open])
	return null
}

function posProfValida(posProf) {
	if (!posProf) return null
	const la = Number(posProf.latitude)
	const lo = Number(posProf.longitude)
	if (!Number.isFinite(la) || !Number.isFinite(lo) || (la === 0 && lo === 0)) return null
	return { la, lo, latitude: la, longitude: lo }
}

/** Próximo ponto a chegar na cotação: menor es.ponto / primeiro da lista. */
function pontoProximo(pontos) {
	if (!Array.isArray(pontos) || !pontos.length) return null
	return [...pontos].sort((a, b) => Number(a.ponto) - Number(b.ponto))[0]
}

/**
 * Mapa Leaflet com:
 * - polyline do serviço (pontos) via OSRM
 * - polyline do profissional até o ponto 1 (próximo) via OSRM
 */
export function MapaCotacaoView({ pontos, posProf, tipoVeiculo = "M", height = "50vh", open = true }) {
	const { dados } = useContext(ContextoGeral)
	const positions = useMemo(() => pontos.map((p) => p.position), [pontos])
	const proximo = useMemo(() => pontoProximo(pontos), [pontos])
	const eu = useMemo(() => posProfValida(posProf), [posProf])

	const [rotaServico, setRotaServico] = useState([])
	const [rotaAtePonto, setRotaAtePonto] = useState([])
	const [carregandoRota, setCarregandoRota] = useState(false)
	const [erroRota, setErroRota] = useState("")

	const chavePontos = useMemo(
		() => pontos.map((p) => `${p.ponto}:${p.la},${p.lo}`).join("|"),
		[pontos]
	)
	const chaveEu = eu ? `${eu.la},${eu.lo}` : ""
	const chaveProximo = proximo ? `${proximo.ponto}:${proximo.la},${proximo.lo}` : ""
	const idProf = dados?.idProf
	const token = dados?.token

	useEffect(() => {
		if (!open) {
			setRotaServico([])
			setRotaAtePonto([])
			setErroRota("")
			return
		}
		if (!idProf || !token) return

		let cancelado = false
		const jobs = []
		setErroRota("")

		// Rota do serviço entre os pontos
		if (pontos.length >= 2) {
			const key = chaveCache(pontos, tipoVeiculo)
			if (cachePolyline.has(key)) {
				setRotaServico(cachePolyline.get(key))
			} else {
				jobs.push(
					buscarPolylineRota(dados, pontos, tipoVeiculo)
						.then((coords) => {
							cachePolyline.set(key, coords)
							if (!cancelado) setRotaServico(coords)
						})
						.catch((err) => {
							console.warn("[MapaCotacao] Falha rota serviço:", err)
							if (!cancelado) {
								setRotaServico([])
								setErroRota("Não foi possível carregar a rota do serviço")
							}
						})
				)
			}
		} else {
			setRotaServico([])
		}

		// Rota do profissional até o próximo ponto (ponto 1 / coleta)
		if (eu && proximo) {
			const trecho = [
				{ la: eu.la, lo: eu.lo, key: "voce" },
				proximo,
			]
			const keyAte = `ate|${chaveCache(trecho, tipoVeiculo)}`
			if (cachePolyline.has(keyAte)) {
				setRotaAtePonto(cachePolyline.get(keyAte))
			} else {
				jobs.push(
					buscarPolylineRota(dados, trecho, tipoVeiculo)
						.then((coords) => {
							cachePolyline.set(keyAte, coords)
							if (!cancelado) setRotaAtePonto(coords)
						})
						.catch((err) => {
							console.warn("[MapaCotacao] Falha rota até ponto:", err)
							if (!cancelado) {
								setRotaAtePonto([])
								setErroRota((prev) => prev || "Não foi possível carregar a rota até o ponto")
							}
						})
				)
			}
		} else {
			setRotaAtePonto([])
		}

		if (!jobs.length) return
		setCarregandoRota(true)
		Promise.all(jobs).finally(() => {
			if (!cancelado) setCarregandoRota(false)
		})

		return () => { cancelado = true }
		// chave* evita loop por nova referência de array/objeto a cada render
	}, [open, chavePontos, tipoVeiculo, chaveEu, chaveProximo, idProf, token])

	const linhaServico = rotaServico.length > 1 ? rotaServico : []
	const linhaAtePonto = rotaAtePonto.length > 1 ? rotaAtePonto : []

	const boundsPositions = useMemo(() => {
		const list = []
		if (linhaServico.length > 1) list.push(...linhaServico)
		if (linhaAtePonto.length > 1) list.push(...linhaAtePonto)
		if (!list.length) list.push(...positions)
		if (eu) list.push([eu.la, eu.lo])
		return list
	}, [linhaServico, linhaAtePonto, positions, eu])

	const center = positions[0] || (eu ? [eu.la, eu.lo] : [-14.24, -51.92])
	const corLinha =
		(typeof window !== "undefined" &&
			getComputedStyle(document.documentElement).getPropertyValue("--primaria")?.trim()) ||
		"#6b21a8"

	if (!pontos.length) {
		return (
			<div
				className="flex items-center justify-center text-sm opacity-75"
				style={{ height, background: "#f3f4f6" }}
			>
				Sem coordenadas para exibir no mapa
			</div>
		)
	}

	return (
		<div style={{ height, width: "100%", position: "relative" }}>
			<MapContainer
				key={`mapa-cotacao-${pontos.map((p) => p.key).join("-")}-${height}`}
				center={center}
				zoom={13}
				scrollWheelZoom={false}
				style={{ height: "100%", width: "100%" }}
			>
				<TileLayer
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				/>
				<FitEInvalidate positions={boundsPositions} open={open} />
				{/* Você → próximo ponto (coleta) */}
				{linhaAtePonto.length > 1 && (
					<Polyline
						positions={linhaAtePonto}
						pathOptions={{ color: "#1976d2", weight: 4, opacity: 0.85, dashArray: "8 10" }}
					/>
				)}
				{/* Rota do serviço entre os pontos */}
				{linhaServico.length > 1 && (
					<Polyline positions={linhaServico} pathOptions={{ color: corLinha, weight: 5, opacity: 0.9 }} />
				)}
				{eu && (
					<Marker position={[eu.la, eu.lo]} icon={iconVoce()}>
						<Popup>Você</Popup>
					</Marker>
				)}
				{pontos.map((p) => (
					<Marker key={String(p.key)} position={p.position} icon={iconNumero(p.ponto)}>
						<Popup>
							<div style={{ fontWeight: 700 }}>Ponto {p.ponto}</div>
							{p.rua && <div style={{ maxWidth: "60vw" }}>{p.rua}</div>}
						</Popup>
					</Marker>
				))}
			</MapContainer>
			{carregandoRota && (
				<div
					className="flex items-center justify-center gap-2"
					style={{
						position: "absolute",
						inset: 0,
						background: "rgba(255,255,255,.55)",
						zIndex: 500,
						pointerEvents: "none",
					}}
				>
					<Spinner />
					<span className="text-sm font-semibold" style={{ color: "var(--primaria)" }}>
						Calculando rota…
					</span>
				</div>
			)}
			{!carregandoRota && erroRota && (
				<div
					className="text-center text-xs px-2 py-1"
					style={{
						position: "absolute",
						left: 0,
						right: 0,
						bottom: 0,
						background: "rgba(255,255,255,.9)",
						color: "var(--bs-danger, #dc3545)",
						zIndex: 500,
					}}
				>
					{erroRota}
				</div>
			)}
		</div>
	)
}

/** Botão discreto "Ver rota" para o card da cotação. */
export function BotaoVerRota({ onClick, className = "" }) {
	return (
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation()
				onClick?.(e)
			}}
			className={`flex items-center gap-1 text-sm font-semibold ${className}`}
			style={{
				color: "var(--primaria)",
				background: "transparent",
				border: "none",
				padding: "4px 0",
				marginTop: 4,
			}}
		>
			<MapIcon sx={{ fontSize: 20 }} />
			Ver rota
		</button>
	)
}

/** Sheet/modal com mapa da cotação / serviço (sempre no body — evita corte por transform do Framer). */
export function MapaCotacaoSheet({ open, onClose, cotacao, posProf }) {
	const pontos = useMemo(() => pontosMapaCotacao(cotacao?.enderecos), [cotacao])
	const proximo = useMemo(() => pontoProximo(pontos), [pontos])
	const tipoVeiculo = cotacao?.["s.mototaxi"] ?? "M"
	const resumo = useMemo(() => {
		const partes = []
		if (pontos.length) partes.push(`${pontos.length} ponto${pontos.length > 1 ? "s" : ""}`)
		const dist = cotacao?.["s.distanciaSemRetorno"] ?? cotacao?.["s.distancia"]
		if (dist) partes.push(`${String(dist).replace(".", ",")} km`)
		return partes.join(" · ")
	}, [pontos, cotacao])

	return (
		<Modal
			open={!!open}
			onClose={onClose}
			aria-labelledby="mapa-rota-titulo"
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: (theme) => theme.zIndex.modal + 2,
			}}
		>
			<Card
				elevation={8}
				sx={{
					display: "flex",
					flexDirection: "column",
					width: "94vw",
					maxWidth: 480,
					maxHeight: "88vh",
					overflow: "hidden",
					outline: "none",
					borderRadius: 2,
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<div
					style={{
						flexShrink: 0,
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: 8,
						padding: "12px 12px 8px",
					}}
				>
					<div style={{ minWidth: 0 }}>
						<div
							id="mapa-rota-titulo"
							style={{
								display: "flex",
								alignItems: "center",
								gap: 4,
								fontWeight: 700,
								color: "var(--primaria)",
								fontSize: "1.15rem",
							}}
						>
							<MapIcon sx={{ fontSize: 22 }} />
							#{cotacao?.["s.id"] ?? "—"}
						</div>
						{resumo ? (
							<div className="text-sm opacity-75 mt-0.5">{resumo}</div>
						) : null}
						<div className="text-xs opacity-60 mt-1 flex items-center gap-1">
							<MdMyLocation size={14} />
							Você → ponto {proximo?.ponto ?? 1} (tracejado) · rota do serviço
						</div>
					</div>
					<IconButton size="small" onClick={onClose} aria-label="Fechar mapa">
						<Close />
					</IconButton>
				</div>

				<MapaCotacaoView
					pontos={pontos}
					posProf={posProf}
					tipoVeiculo={tipoVeiculo}
					height="58vh"
					open={!!open}
				/>

				<div style={{ flexShrink: 0, padding: 8, textAlign: "center" }}>
					<button
						type="button"
						className="w-full py-2 font-semibold"
						style={{ color: "var(--primaria)", background: "transparent", border: "none" }}
						onClick={onClose}
					>
						Fechar
					</button>
				</div>
			</Card>
		</Modal>
	)
}
